import { useParams } from "@tanstack/react-router"
import { useCallback, useEffect, useRef } from "react"
import type { RefObject } from "react"
import type { Client } from "urql"
import { useShallow } from "zustand/react/shallow"

import {
  SALES_ORDER_DEFAULT_DATE_RANGE_DAYS,
  SALES_ORDER_FILTER_KEYS,
  SALES_ORDER_PRESERVED_EMPTY_FILTER_KEYS,
} from "../constants"
import { useSalesOrderSearchStore } from "../stores/useSalesOrderSearchStore"

import { type SalesOrder, SalesOrderDateRangeType } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"
import type { SalesOrderSearchParams } from "@/screens/CustomerOrders/schemas"
import { DEFAULT_PAGE_SIZE } from "@/stores/createSearchStore"
import { getDateDaysAgo } from "@/utils/date"

const ROUTE = "/app/orders/$companyId/$shipToLocationId/_layout/open-sales-orders"

interface UseSalesOrderListReturn {
  salesOrders: SalesOrder[]
  isFetching: boolean
  error: Error | undefined
  companyId: string
  shipToLocationId: string
  searchParams: SalesOrderSearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  handleScroll: () => void
  handleSearchChange: (query: string) => void
  handleFilterChange: <K extends keyof SalesOrderSearchParams>(key: K, value: SalesOrderSearchParams[K]) => void
  handleFiltersChange: (updates: Partial<SalesOrderSearchParams>) => void
  handleFilterRemove: (
    keyOrKeys: keyof SalesOrderSearchParams | (keyof SalesOrderSearchParams)[],
    value?: string | number
  ) => void
  handleFilterClear: () => void
  refetch: () => void
}

export const useSalesOrderList = (): UseSalesOrderListReturn => {
  const { companyId, shipToLocationId } = useParams({ from: ROUTE })

  const store = useSalesOrderSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )

  const { executeSearch: storeExecuteSearch, loadMore: storeLoadMore } = store

  // Depend on the store's action functions directly, not the whole `store` object — `store` is
  // a new reference every time data/fetching/error change (useShallow selects them together),
  // which would give this callback a new identity on every fetch and retrigger
  // useListPageBase's fetch effect in a loop. The action functions themselves never change.
  const executeSearch = useCallback(
    (client: Client, params: SalesOrderSearchParams, force?: boolean) => {
      // A forced search is the post-save refetch, which raises the page size to keep loaded rows;
      // any other search starts a fresh list, so it goes back to the default page size.
      if (!force) useSalesOrderSearchStore.setState({ limit: DEFAULT_PAGE_SIZE })
      storeExecuteSearch(client, { ...params, companyId, shipToLocationId }, force)
    },
    [storeExecuteSearch, companyId, shipToLocationId]
  )

  const loadMore = useCallback(
    (client: Client, params: SalesOrderSearchParams) => {
      storeLoadMore(client, { ...params, companyId, shipToLocationId })
    },
    [storeLoadMore, companyId, shipToLocationId]
  )

  const {
    data: salesOrders,
    isFetching,
    error,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSearchChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
    refetch: refetchFirstPage,
  } = useListPageBase<SalesOrder, SalesOrderSearchParams>({
    route: ROUTE,
    store: { ...store, executeSearch, loadMore },
    filterKeys: SALES_ORDER_FILTER_KEYS,
    preservedEmptyFilterKeys: SALES_ORDER_PRESERVED_EMPTY_FILTER_KEYS,
  })

  // The base refetch restarts at offset 0 with one page, which would drop rows loaded by scrolling —
  // including the one just saved. Raising the page size to cover everything loaded keeps those rows
  // (the store's load-more offset arithmetic stays consistent because it steps by the same limit).
  const refetch = useCallback(() => {
    const loadedCount = useSalesOrderSearchStore.getState().data.length
    const limit = Math.max(DEFAULT_PAGE_SIZE, Math.ceil(loadedCount / DEFAULT_PAGE_SIZE) * DEFAULT_PAGE_SIZE)
    useSalesOrderSearchStore.setState({ limit })
    refetchFirstPage()
  }, [refetchFirstPage])

  // Seeds the "last 30 days, Confirmation Sent Date" default into the URL once on tab load, so
  // it's a real, visible, clearable filter (radio/date fields + chip) rather than a hidden
  // query-time patch. Only fires when the user hasn't already got a date filter of their own —
  // guarded to run once per mount so Filter → Clear All isn't immediately re-seeded.
  const hasSeededDefaultDateFilter = useRef(false)
  useEffect(() => {
    if (hasSeededDefaultDateFilter.current) return
    hasSeededDefaultDateFilter.current = true

    if (!searchParams.dateFrom && !searchParams.dateTo && !searchParams.dateRangeType) {
      handleFiltersChange({
        dateFrom: getDateDaysAgo(SALES_ORDER_DEFAULT_DATE_RANGE_DAYS),
        dateTo: getDateDaysAgo(0),
        dateRangeType: SalesOrderDateRangeType.ConfirmationSentDate,
      })
    }
  }, [searchParams, handleFiltersChange])

  return {
    salesOrders,
    isFetching,
    error,
    companyId,
    shipToLocationId,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSearchChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
    refetch,
  }
}
